import React, { useMemo, useState } from "react";
import {
    DndContext,
    KeyboardSensor,
    MouseSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { Active } from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { SortableItem } from "./SortableItem";
import { SortableOverlay } from "./SortableOverlay";
import { BaseItem, Props } from "./SortableList.types";
import { DragHandle } from "./SortableDragHandle";
import { Box } from "@mui/system";

export function SortableList<T extends BaseItem>({
    items,
    onChange,
    onDragAndDropChanged,
    renderItem,
}: Props<T>) {
    const [active, setActive] = useState<Active>();
    const activeItem = useMemo(
        () => items.find((item) => item.id === active?.id),
        [active, items],
    );
    const sensors = useSensors(
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 350,
                tolerance: 50,
            },
        }),
        useSensor(MouseSensor, {
            activationConstraint: {
                delay: 350,
                tolerance: 50,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
        useSensor(PointerSensor),
    );

    return (
        <DndContext
            sensors={sensors}
            onDragStart={({ active }) => {
                onDragAndDropChanged(true);
                setActive(active);
            }}
            onDragEnd={({ active, over }) => {
                if (over && active.id !== over?.id) {
                    const activeIndex = items.findIndex(
                        (item) => item.id === active.id,
                    );
                    const overIndex = items.findIndex(
                        (item) => item.id === over.id,
                    );

                    onChange(arrayMove(items, activeIndex, overIndex));
                }
                onDragAndDropChanged(false);
                setActive(undefined);
            }}
            onDragCancel={() => {
                onDragAndDropChanged(false);
                setActive(undefined);
            }}
        >
            <SortableContext items={items}>
                {items.map((item) => (
                    <Box style={{ touchAction: "none" }} key={item.id}>
                        <SortableItem id={item.id}>
                            {renderItem(item)}
                        </SortableItem>
                    </Box>
                ))}
            </SortableContext>
            <SortableOverlay>
                {activeItem ? renderItem(activeItem) : null}
            </SortableOverlay>
        </DndContext>
    );
}

SortableList.Item = SortableItem;
SortableList.DragHandle = DragHandle;
